import { useEffect } from 'react';

interface SEOProps {
    title?: string;
    description?: string;
    image?: string;
    url?: string;
    type?: string;
}

/**
 * SEO component to dynamically update page metadata.
 * Usage: <SEO title="Genesis 1" description="Read Genesis 1 in BibleGlen" />
 */
export function SEO({
    title,
    description = "Explore the Bible with rich historical, cultural, and literary context. BibleGlen is your interactive expedition through the Word of God.",
    image = "https://bibleglen.com/logo.png",
    url = "https://bibleglen.com",
    type = "website"
}: SEOProps) {
    useEffect(() => {
        // Update Title
        const fullTitle = title ? `${title} | BibleGlen` : "BibleGlen - Your Biblical Discovery Expedition";
        document.title = fullTitle;

        const updateMeta = (name: string, content: string, attr: 'name' | 'property' = 'name') => {
            let element = document.querySelector(`${attr === 'name' ? 'meta[name="' : 'meta[property="'}${name}"]`);
            if (!element) {
                element = document.createElement('meta');
                element.setAttribute(attr, name);
                document.head.appendChild(element);
            }
            element.setAttribute('content', content);
        };

        const updateCanonical = (href: string) => {
            let element = document.querySelector('link[rel="canonical"]');
            if (!element) {
                element = document.createElement('link');
                element.setAttribute('rel', 'canonical');
                document.head.appendChild(element);
            }
            element.setAttribute('href', href);
        };

        // Standard Meta Tags
        updateMeta('description', description);
        updateCanonical(url);

        // Open Graph / Facebook
        updateMeta('og:type', type, 'property');
        updateMeta('og:url', url, 'property');
        updateMeta('og:title', title || "BibleGlen", 'property');
        updateMeta('og:description', description, 'property');
        updateMeta('og:image', image, 'property');

        // Twitter
        updateMeta('twitter:card', 'summary_large_image');
        updateMeta('twitter:url', url);
        updateMeta('twitter:title', title || "BibleGlen");
        updateMeta('twitter:description', description);
        updateMeta('twitter:image', image);

    }, [title, description, image, url, type]);

    return null;
}
